// src/ine/ine_config/pages/IneConfigPage.jsx
import React, { useEffect, useState } from "react";
import AutoDismissAlert from "../../../components/autoDismiss";
import { API_URLS } from "../../../api/apiConfig";
import { getWithAuth, postWithAuth } from "../../../api/api";
import { useIneConfig } from "../ineConfigContext";
import {
  INE_CATEGORIAS_APARTAMENTOS_BY_PROVINCIA,
  INE_PROVINCIAS_APARTAMENTOS,
} from "../utils/ineApartamentos";

const CIF_NIF_REGEX = /^[A-Z0-9][0-9]{7}[A-Z0-9]$/;
const POSTAL_CODE_REGEX = /^[0-9]{5}$/;

const emptyForm = {
  bussiness_name: "",
  establishment_name: "",
  cif_nif: "",
  registrer_name: "",
  address: "",
  postal_code: "",
  village: "",
  city: "",
  province: "",
  phone_1: "",
  phone_2: "",
  fax_1: "",
  fax_2: "",
  type_of_establishment: "",
  category_type: "",
  web_page: "",

  // 👇 NUEVOS
  plazas_sin_supletorias: 0,
  number_of_rooms: 0,
  personal_no_remunerado: 0,
  personal_remunerado_fijo: 0,
  personal_remunerado_eventual: 0,
  provincia_apartamento: "",
  categoria_apartamento: "",
};

const numericFields = new Set([
  "plazas_sin_supletorias",
  "number_of_rooms",
  "personal_no_remunerado",
  "personal_remunerado_fijo",
  "personal_remunerado_eventual",
]);

const IneConfigPage = () => {
  const INE = useIneConfig();

  // enums
  const [typeOptions, setTypeOptions] = useState([]); // [{value,label}]
  const [categoryOptions, setCategoryOptions] = useState([]); // [{value,label}]
  const [isLoadingEnums, setIsLoadingEnums] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [apartmentCategoryOptions, setApartmentCategoryOptions] = useState([]);

  // formulario
  const [form, setForm] = useState({ ...emptyForm });
  const [errors, setErrors] = useState({});

  const handleApiError = (detailFallback) => (err) => {
    console.error(err);
    INE.setMessage(
      detailFallback || "Ha ocurrido un error conectando con el servidor"
    );
    INE.setAlertType("danger");
    INE.openAlertHeader();
  };

  // Normalizadores por si backend devuelve {type} o {value,label} / {code,label}
  const normalizeTypes = (arr) =>
    (arr || []).map((t) =>
      t?.value
        ? { value: t.value, label: t.label ?? t.value }
        : { value: t.type, label: t.type }
    );

  const normalizeCategories = (arr) =>
    (arr || []).map((c) => {
      // backend típico: [{ category: "H1" }, ...]
      if (c?.category) return { value: c.category, label: c.category };
      // compatibilidad
      if (c?.value) return { value: c.value, label: c.label ?? c.value };
      if (c?.code) return { value: c.code, label: c.label ?? c.code };
      return { value: String(c ?? ""), label: String(c ?? "") };
    });

  const fetchTypes = async () => {
    setIsLoadingEnums(true);
    try {
      const resp = await getWithAuth(API_URLS.INE_GET_EOEH_TYPES);
      if (resp?.message === "success") {
        setTypeOptions(normalizeTypes(resp.data));
      } else {
        INE.setMessage(resp?.detail || "Error al obtener tipos INE");
        INE.setAlertType("danger");
        INE.openAlertHeader();
      }
    } catch (e) {
      handleApiError("Error al obtener tipos INE")(e);
    } finally {
      setIsLoadingEnums(false);
    }
  };

  const fetchCategoriesForType = async (typeValue) => {
    if (!typeValue) {
      setCategoryOptions([]);
      return;
    }
    setIsLoadingCategories(true);
    try {
      const resp = await getWithAuth(
        API_URLS.INE_GET_EOEH_CATEGORIES(typeValue)
      );
      if (resp?.message === "success") {
        setCategoryOptions(normalizeCategories(resp.data));
      } else {
        INE.setMessage(
          resp?.detail || `Error al obtener categorías para ${typeValue}`
        );
        INE.setAlertType("danger");
        INE.openAlertHeader();
        setCategoryOptions([]);
      }
    } catch (e) {
      handleApiError(`Error al obtener categorías para ${typeValue}`)(e);
      setCategoryOptions([]);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const preload = async () => {
    try {
      const resp = await getWithAuth(API_URLS.INE_GET_CONFIG);
      if (resp?.message === "success" && resp?.data) {
        const data = resp.data;
        setForm({
          bussiness_name: data.bussiness_name || "",
          establishment_name: data.establishment_name || "",
          cif_nif: (data.cif_nif || "").toUpperCase(),
          registrer_name: data.registrer_name || "",
          address: data.address || "",
          postal_code: data.postal_code || "",
          village: data.village || "",
          city: data.city || "",
          province: data.province || "",
          phone_1: data.phone_1 || "",
          phone_2: data.phone_2 || "",
          fax_1: data.fax_1 || "",
          fax_2: data.fax_2 || "",
          type_of_establishment: data.type_of_establishment || "",
          category_type: data.category_type || "",
          web_page: data.web_page || "",

          // 👇 NUEVOS (con fallback numérico)
          plazas_sin_supletorias: Number(data.plazas_sin_supletorias ?? 0),
          number_of_rooms: Number(data.number_of_rooms ?? 0),
          personal_no_remunerado: Number(data.personal_no_remunerado ?? 0),
          personal_remunerado_fijo: Number(data.personal_remunerado_fijo ?? 0),
          personal_remunerado_eventual: Number(
            data.personal_remunerado_eventual ?? 0
          ),
          provincia_apartamento: data.provincia_apartamento || "",
          categoria_apartamento: data.categoria_apartamento || "",
        });

        // si ya hay tipo cargado, precarga categorías
        if (data.type_of_establishment) {
          await fetchCategoriesForType(data.type_of_establishment);
        }
        const provAp = data.provincia_apartamento || "";
        if (provAp) {
          const cats =
            INE_CATEGORIAS_APARTAMENTOS_BY_PROVINCIA[provAp] ||
            INE_CATEGORIAS_APARTAMENTOS_BY_PROVINCIA["*"] ||
            [];
          setApartmentCategoryOptions(cats);
        }
      }
    } catch (e) {
      console.warn("No se pudo precargar INE config:", e);
    }
  };

  useEffect(() => {
    fetchTypes();
    preload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;

    setIsSaving(true);
    try {
      const payload = {
        bussiness_name: (form.bussiness_name ?? "").trim(),
        establishment_name: (form.establishment_name ?? "").trim(),
        cif_nif: (form.cif_nif ?? "").toUpperCase().trim(),
        registrer_name: (form.registrer_name ?? "").trim(),
        address: (form.address ?? "").trim(),
        postal_code: (form.postal_code ?? "").trim(),
        village: (form.village ?? "").trim() || null, // opcional
        city: (form.city ?? "").trim(),
        province: (form.province ?? "").trim(),
        phone_1: (form.phone_1 ?? "").trim(),
        phone_2: (form.phone_2 ?? "").trim() || null, // opcional
        fax_1: (form.fax_1 ?? "").trim() || null, // opcional
        fax_2: (form.fax_2 ?? "").trim() || null, // opcional
        type_of_establishment: form.type_of_establishment, // enum
        category_type: form.category_type || null, // puede ser opcional
        web_page: (form.web_page ?? "").trim() || null,

        // 👇 NUEVOS (forzamos número)
        plazas_sin_supletorias: Number(form.plazas_sin_supletorias) || 0,
        number_of_rooms: Number(form.number_of_rooms) || 0,
        personal_no_remunerado: Number(form.personal_no_remunerado) || 0,
        personal_remunerado_fijo: Number(form.personal_remunerado_fijo) || 0,
        personal_remunerado_eventual:
          Number(form.personal_remunerado_eventual) || 0,
        provincia_apartamento:
          (form.provincia_apartamento ?? "").trim() || null,
        categoria_apartamento:
          (form.categoria_apartamento ?? "").trim() || null,
      };

      const resp = await postWithAuth(API_URLS.INE_UPDATE_CONFIG, payload);

      if (resp?.message === "success") {
        INE.setMessage("Configuración INE guardada correctamente.");
        INE.setAlertType("success");
        INE.openAlertHeader();
      } else {
        const detail =
          resp?.detail ||
          (Array.isArray(resp?.error_list) && resp.error_list.join(", ")) ||
          "No se pudo guardar la configuración INE.";
        INE.setMessage(detail);
        INE.setAlertType("danger");
        INE.openAlertHeader();
      }
    } catch (e) {
      console.error(e);
      if (
        e?.response?.status === 422 &&
        Array.isArray(e?.response?.data?.detail)
      ) {
        const fieldErrors = {};
        e.response.data.detail.forEach((d) => {
          const loc = d?.loc?.[d.loc.length - 1];
          if (loc) fieldErrors[loc] = d?.msg || "Valor inválido";
        });
        setErrors((prev) => ({ ...prev, ...fieldErrors }));
        INE.setMessage("Revisa los campos marcados en rojo.");
      } else {
        INE.setMessage("Ha ocurrido un error guardando la configuración.");
      }
      INE.setAlertType("danger");
      INE.openAlertHeader();
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;

    // campos numéricos: permitimos vacío y solo dígitos
    const nextValue = numericFields.has(name)
      ? value === ""
        ? ""
        : String(value).replace(/[^\d]/g, "")
      : value;

    // caso especial: provincia_apartamento → recalculamos categorías de llaves
    if (name === "provincia_apartamento") {
      const cats =
        INE_CATEGORIAS_APARTAMENTOS_BY_PROVINCIA[nextValue] ||
        INE_CATEGORIAS_APARTAMENTOS_BY_PROVINCIA["*"] ||
        [];

      setApartmentCategoryOptions(cats);

      setForm((f) => ({
        ...f,
        provincia_apartamento: nextValue,
        // reseteamos categoría si cambiamos de provincia
        categoria_apartamento: "",
      }));

      return;
    }

    setForm((f) => ({ ...f, [name]: nextValue }));

    // si cambia el tipo, resetea categoría de hoteles y recarga
    if (name === "type_of_establishment") {
      setForm((f) => ({ ...f, category_type: "" }));
      await fetchCategoriesForType(nextValue);
    }
  };

  const validate = () => {
    const e = {};
    const norm = (v) => (v ?? "").trim();
    const isNonNegInt = (v) =>
      String(v).trim() !== "" && Number.isInteger(Number(v)) && Number(v) >= 0;

    // establishment_name: obligatorio, <= 100
    if (!norm(form.establishment_name)) e.establishment_name = "Obligatorio";
    else if (norm(form.establishment_name).length > 100)
      e.establishment_name = "Máximo 100 caracteres";

    // cif_nif: obligatorio, 9, patrón
    if (!norm(form.cif_nif)) e.cif_nif = "Obligatorio";
    else if (norm(form.cif_nif).length !== 9)
      e.cif_nif = "Debe tener 9 caracteres";
    else if (!CIF_NIF_REGEX.test(norm(form.cif_nif).toUpperCase()))
      e.cif_nif = "Formato inválido (p.ej: A1234567B)";

    // registrer_name (opcional max 50)
    if (norm(form.registrer_name) && norm(form.registrer_name).length > 50)
      e.registrer_name = "Máximo 50 caracteres";

    // address: obligatorio, <= 100
    if (!norm(form.address)) e.address = "Obligatorio";
    else if (norm(form.address).length > 100)
      e.address = "Máximo 100 caracteres";

    // postal_code: obligatorio, 5 dígitos
    if (!norm(form.postal_code)) e.postal_code = "Obligatorio";
    else if (!POSTAL_CODE_REGEX.test(norm(form.postal_code)))
      e.postal_code = "5 dígitos (0-9)";

    // village: opcional 1..100 si viene
    if (
      norm(form.village) &&
      !(norm(form.village).length >= 1 && norm(form.village).length <= 100)
    )
      e.village = "Entre 1 y 100 caracteres";

    // city: obligatorio 1..33
    if (!norm(form.city)) e.city = "Obligatorio";
    else if (!(norm(form.city).length >= 1 && norm(form.city).length <= 33))
      e.city = "Entre 1 y 33 caracteres";

    // province: obligatorio 1..25
    if (!norm(form.province)) e.province = "Obligatorio";
    else if (
      !(norm(form.province).length >= 1 && norm(form.province).length <= 25)
    )
      e.province = "Entre 1 y 25 caracteres";

    // phone_1: obligatorio 9..13
    if (!norm(form.phone_1)) e.phone_1 = "Obligatorio";
    else if (
      !(norm(form.phone_1).length >= 9 && norm(form.phone_1).length <= 13)
    )
      e.phone_1 = "Entre 9 y 13 caracteres";

    // phone_2: opcional 9..13
    if (
      norm(form.phone_2) &&
      !(norm(form.phone_2).length >= 9 && norm(form.phone_2).length <= 13)
    )
      e.phone_2 = "Entre 9 y 13 caracteres";

    // fax_1 y fax_2: opcionales 9..13
    if (
      norm(form.fax_1) &&
      !(norm(form.fax_1).length >= 9 && norm(form.fax_1).length <= 13)
    )
      e.fax_1 = "Entre 9 y 13 caracteres";
    if (
      norm(form.fax_2) &&
      !(norm(form.fax_2).length >= 9 && norm(form.fax_2).length <= 13)
    )
      e.fax_2 = "Entre 9 y 13 caracteres";

    // type_of_establishment: obligatorio
    if (!norm(form.type_of_establishment))
      e.type_of_establishment = "Selecciona un tipo";

    // 👇 Validaciones numéricas
    if (!isNonNegInt(form.plazas_sin_supletorias))
      e.plazas_sin_supletorias = "Introduce un número entero ≥ 0";

    [
      "personal_no_remunerado",
      "personal_remunerado_fijo",
      "personal_remunerado_eventual",
    ].forEach((k) => {
      if (!isNonNegInt(form[k])) e[k] = "Número entero ≥ 0";
    });

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  return (
    <>
      {INE.showAlertHeader && (
        <div className="mb-5">
          <AutoDismissAlert
            message={INE.message}
            alertType={INE.alertType}
            onClose={INE.closeAlertHeader}
          />
        </div>
      )}

      <div className="d-flex align-items-center justify-content-between mb-6">
        <div className="card-title">
          <h3 className="mb-0">Conexión INE</h3>
        </div>
      </div>

      {isLoadingEnums ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "30vh" }}
        >
          <div
            className="spinner-grow text-color-verde-wofyrooms"
            style={{ width: "3.5rem", height: "3.5rem" }}
          />
        </div>
      ) : (
        <form onSubmit={submit} noValidate>
          <div className="row mb-10">
            {/* Izquierda */}
            <div className="col-md-6 pb-10 pb-lg-0">
              <h2>Identificación del Establecimiento</h2>
              <div className="row g-6 mt-2">
                <div className="col-12">
                  <label className="form-label">Razón social</label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.bussiness_name ? "is-invalid" : ""
                    }`}
                    name="bussiness_name"
                    value={form.bussiness_name}
                    onChange={handleChange}
                  />
                  {errors.bussiness_name && (
                    <div className="invalid-feedback">
                      {errors.bussiness_name}
                    </div>
                  )}
                </div>

                <div className="col-12">
                  <label className="form-label required">
                    Nombre del establecimiento
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.establishment_name ? "is-invalid" : ""
                    }`}
                    name="establishment_name"
                    value={form.establishment_name}
                    onChange={handleChange}
                  />
                  {errors.establishment_name && (
                    <div className="invalid-feedback">
                      {errors.establishment_name}
                    </div>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label required">CIF/NIF</label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.cif_nif ? "is-invalid" : ""
                    }`}
                    name="cif_nif"
                    value={form.cif_nif}
                    onChange={handleChange}
                    maxLength={9}
                    placeholder="A1234567B"
                  />
                  {errors.cif_nif && (
                    <div className="invalid-feedback">{errors.cif_nif}</div>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label required">
                    Número de registro
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.registrer_name ? "is-invalid" : ""
                    }`}
                    name="registrer_name"
                    value={form.registrer_name}
                    onChange={handleChange}
                    maxLength={50}
                  />
                  {errors.registrer_name && (
                    <div className="invalid-feedback">
                      {errors.registrer_name}
                    </div>
                  )}
                </div>

                <div className="col-12">
                  <label className="form-label required">Dirección</label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.address ? "is-invalid" : ""
                    }`}
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    maxLength={100}
                  />
                  {errors.address && (
                    <div className="invalid-feedback">{errors.address}</div>
                  )}
                </div>

                <div className="col-md-4">
                  <label className="form-label required">Cód. Postal</label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.postal_code ? "is-invalid" : ""
                    }`}
                    name="postal_code"
                    value={form.postal_code}
                    onChange={handleChange}
                    maxLength={5}
                    inputMode="numeric"
                  />
                  {errors.postal_code && (
                    <div className="invalid-feedback">{errors.postal_code}</div>
                  )}
                </div>

                <div className="col-md-4">
                  <label className="form-label">Localidad</label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.village ? "is-invalid" : ""
                    }`}
                    name="village"
                    value={form.village}
                    onChange={handleChange}
                    maxLength={100}
                  />
                  {errors.village && (
                    <div className="invalid-feedback">{errors.village}</div>
                  )}
                </div>

                <div className="col-md-4">
                  <label className="form-label required">Municipio</label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.city ? "is-invalid" : ""
                    }`}
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    maxLength={33}
                  />
                  {errors.city && (
                    <div className="invalid-feedback">{errors.city}</div>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label required">Provincia</label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.province ? "is-invalid" : ""
                    }`}
                    name="province"
                    value={form.province}
                    onChange={handleChange}
                    maxLength={25}
                  />
                  {errors.province && (
                    <div className="invalid-feedback">{errors.province}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Derecha */}
            <div className="col-md-6">
              <h2>Contacto y Clasificación</h2>
              <div className="row g-6 mt-2">
                <div className="col-md-6">
                  <label className="form-label required">
                    Teléfono principal
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.phone_1 ? "is-invalid" : ""
                    }`}
                    name="phone_1"
                    value={form.phone_1}
                    onChange={handleChange}
                    maxLength={13}
                  />
                  {errors.phone_1 && (
                    <div className="invalid-feedback">{errors.phone_1}</div>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label">Teléfono secundario</label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.phone_2 ? "is-invalid" : ""
                    }`}
                    name="phone_2"
                    value={form.phone_2}
                    onChange={handleChange}
                    maxLength={13}
                  />
                  {errors.phone_2 && (
                    <div className="invalid-feedback">{errors.phone_2}</div>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label required">Fax 1</label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.fax_1 ? "is-invalid" : ""
                    }`}
                    name="fax_1"
                    value={form.fax_1}
                    onChange={handleChange}
                    maxLength={13}
                  />
                  {errors.fax_1 && (
                    <div className="invalid-feedback">{errors.fax_1}</div>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label">Fax 2</label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.fax_2 ? "is-invalid" : ""
                    }`}
                    name="fax_2"
                    value={form.fax_2}
                    onChange={handleChange}
                    maxLength={13}
                  />
                  {errors.fax_2 && (
                    <div className="invalid-feedback">{errors.fax_2}</div>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label required">
                    Tipo de establecimiento
                  </label>
                  <select
                    className={`form-select ${
                      errors.type_of_establishment ? "is-invalid" : ""
                    }`}
                    name="type_of_establishment"
                    value={form.type_of_establishment}
                    onChange={handleChange}
                  >
                    <option value="">Selecciona…</option>
                    {typeOptions.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  {errors.type_of_establishment && (
                    <div className="invalid-feedback">
                      {errors.type_of_establishment}
                    </div>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label">Categoría</label>
                  <select
                    className="form-select"
                    name="category_type"
                    value={form.category_type}
                    onChange={handleChange}
                    disabled={
                      !form.type_of_establishment || isLoadingCategories
                    }
                  >
                    <option value="">
                      {isLoadingCategories
                        ? "Cargando…"
                        : form.type_of_establishment
                        ? "Selecciona…"
                        : "Elige un tipo primero"}
                    </option>
                    {categoryOptions.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    Provincia apartamentos (INE)
                  </label>
                  <select
                    className="form-select"
                    name="provincia_apartamento"
                    value={form.provincia_apartamento}
                    onChange={handleChange}
                  >
                    <option value="">Selecciona…</option>
                    {INE_PROVINCIAS_APARTAMENTOS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    Categoría apartamentos (Llaves)
                  </label>
                  <select
                    className="form-select"
                    name="categoria_apartamento"
                    value={form.categoria_apartamento}
                    onChange={handleChange}
                    disabled={!form.provincia_apartamento}
                  >
                    <option value="">
                      {form.provincia_apartamento
                        ? "Selecciona…"
                        : "Selecciona provincia primero"}
                    </option>
                    {apartmentCategoryOptions.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Página Web</label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.web_page ? "is-invalid" : ""
                    }`}
                    name="web_page"
                    value={form.web_page}
                    onChange={handleChange}
                    maxLength={100}
                  />
                  {errors.web_page && (
                    <div className="invalid-feedback">{errors.web_page}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Capacidad y Personal */}
          <div className="row mt-2 mb-10">
            <div className="col-md-6">
              <h2>Capacidad</h2>
              <div className="row g-6 mt-2">
                <div className="col-md-12">
                  <label className="form-label required">
                    Plazas sin supletorias
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className={`form-control ${
                      errors.plazas_sin_supletorias ? "is-invalid" : ""
                    }`}
                    name="plazas_sin_supletorias"
                    value={form.plazas_sin_supletorias}
                    onChange={handleChange}
                  />
                  {errors.plazas_sin_supletorias && (
                    <div className="invalid-feedback">
                      {errors.plazas_sin_supletorias}
                    </div>
                  )}
                </div>
                <div className="col-md-12">
                  <label className="form-label required">
                    Habitaciones Totales
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className={`form-control ${
                      errors.number_of_rooms ? "is-invalid" : ""
                    }`}
                    name="number_of_rooms"
                    value={form.number_of_rooms}
                    onChange={handleChange}
                  />
                  {errors.number_of_rooms && (
                    <div className="invalid-feedback">
                      {errors.number_of_rooms}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <h2>Personal</h2>
              <div className="row g-6 mt-2">
                <div className="col-md-12">
                  <label className="form-label required">
                    Personal no remunerado
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className={`form-control ${
                      errors.personal_no_remunerado ? "is-invalid" : ""
                    }`}
                    name="personal_no_remunerado"
                    value={form.personal_no_remunerado}
                    onChange={handleChange}
                  />
                  {errors.personal_no_remunerado && (
                    <div className="invalid-feedback">
                      {errors.personal_no_remunerado}
                    </div>
                  )}
                </div>

                <div className="col-md-12">
                  <label className="form-label required">
                    Personal remunerado fijo
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className={`form-control ${
                      errors.personal_remunerado_fijo ? "is-invalid" : ""
                    }`}
                    name="personal_remunerado_fijo"
                    value={form.personal_remunerado_fijo}
                    onChange={handleChange}
                  />
                  {errors.personal_remunerado_fijo && (
                    <div className="invalid-feedback">
                      {errors.personal_remunerado_fijo}
                    </div>
                  )}
                </div>

                <div className="col-md-12">
                  <label className="form-label required">
                    Personal remunerado eventual
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className={`form-control ${
                      errors.personal_remunerado_eventual ? "is-invalid" : ""
                    }`}
                    name="personal_remunerado_eventual"
                    value={form.personal_remunerado_eventual}
                    onChange={handleChange}
                  />
                  {errors.personal_remunerado_eventual && (
                    <div className="invalid-feedback">
                      {errors.personal_remunerado_eventual}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="notice d-flex bg-light-primary rounded border-primary border border-dashed p-6 mb-8">
            <i className="ki-outline ki-design-1 fs-2tx text-primary me-4"></i>
            <div className="d-flex flex-stack flex-grow-1">
              <div className="fw-semibold">
                <div className="fs-6 text-gray-700">
                  Cambiar estos valores puede requerir actualizar la información
                  enviada al INE.
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-light btn-active-light-primary"
              onClick={() => setForm({ ...emptyForm })}
            >
              Deshacer cambios
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSaving}
            >
              {isSaving ? "Guardando…" : "Guardar configuración"}
            </button>
          </div>
        </form>
      )}
    </>
  );
};

export default IneConfigPage;
